import type { FC, ReactNode } from "react";
import { createContext, useEffect, useState } from "react";

import type { MfaOption } from "@repo/ui";

interface AuthContextProps {
	rememberMe: boolean;
	setRememberMe: (value: boolean) => void;

	username: string;
	setUsername: (value: string) => void;

	returnUrl?: string;
	setReturnUrl?: (value: string) => void;

	selectedMfaType?: MfaOption;
	setSelectedMfaType?: (value: MfaOption) => void;

	clearStorage: () => void;
}

export const AuthContext = createContext<AuthContextProps>({
	rememberMe: false,
	setRememberMe: () => null,

	username: "",
	setUsername: () => null,

	returnUrl: undefined,
	setReturnUrl: () => null,

	selectedMfaType: undefined,
	setSelectedMfaType: () => null,

	clearStorage: () => null,
});

interface Props {
	children: ReactNode;
}

export const AuthContextProvider: FC<Props> = ({ children }) => {
	const [rememberMe, setRememberMe] = useState<boolean>(localStorage.getItem("rememberMe") === "true");
	const [username, setUsername] = useState<string>(localStorage.getItem("username") ?? "");
	const [returnUrl, setReturnUrl] = useState<string | undefined>(localStorage.getItem("returnUrl") ?? undefined);
	const [selectedMfaType, setSelectedMfaType] = useState<MfaOption | undefined>(
		(parseInt(localStorage.getItem("selectedMfaType")) as MfaOption) ?? undefined,
	);

	useEffect(() => {
		localStorage.setItem("rememberMe", rememberMe.toString());
		localStorage.setItem("username", username);
		localStorage.setItem("returnUrl", returnUrl);
		localStorage.setItem("selectedMfaType", selectedMfaType.toString());
	}, [rememberMe, returnUrl, selectedMfaType, username]);

	function clearStorage() {
		localStorage.removeItem("rememberMe");
		localStorage.removeItem("username");
		localStorage.removeItem("returnUrl");
		localStorage.removeItem("selectedMfaType");
	}

	return (
		<AuthContext.Provider
			value={{
				rememberMe,
				setRememberMe,
				username,
				setUsername,
				returnUrl,
				setReturnUrl,
				selectedMfaType,
				setSelectedMfaType,
				clearStorage,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};
